import { useEffect } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { createRootRouteWithContext, HeadContent, Outlet, type RouterHistory, type useMatches } from '@tanstack/react-router';
import type { useActiveRealmAPIContext } from '@interxfi/api-client/realm-context';
import { useFrameLoader } from '@interxfi/design-system/components/frame';
import type { PortalTabbedNavigationGroups } from '../components/router/router-tabbed-navigation';
import {
  type RouteGuardOptions,
  type RouteGuardOptionsWithAdditionalRedirectContext,
  routeGuardRequireAuthentication,
  routeGuardValidRealmAccess,
} from '../util/routing/guard';
import { getRedirectParameter } from '../util/routing/redirect';

function reduceMatchAuthRequirements(matches: ReturnType<typeof useMatches>): RouteGuardOptionsWithAdditionalRedirectContext | undefined {
  return matches.reduce<RouteGuardOptionsWithAdditionalRedirectContext | undefined>((acc, curr) => {
    if (curr.staticData && 'guard' in curr.staticData) {
      const guard = curr.staticData.guard as RouteGuardOptions['guard'];

      if (('auth' in guard && guard.auth) || ('access' in guard && guard.access)) {
        const newAcc = acc || { guard: {} };

        if ('auth' in guard && guard.auth) {
          // Always take the furthest down auth parameters
          newAcc.guard.auth = guard.auth;

          if (guard.auth.redirectToIfMfaRequirementNotMet) {
            newAcc.guard.auth.redirectToIfMfaRequirementNotMet = guard.auth.redirectToIfMfaRequirementNotMet;
          }

          if (curr.search) {
            newAcc.additionalRedirectContext = curr.search;
          }
        }

        if ('access' in guard && guard.access) {
          if (!newAcc.guard.access) {
            newAcc.guard.access = { allowedTenantTypes: guard.access.allowedTenantTypes };
            return newAcc;
          }

          newAcc.guard.access.allowedTenantTypes = newAcc.guard.access.allowedTenantTypes.filter((currentAllowedAccess) =>
            guard.access?.allowedTenantTypes.includes(currentAllowedAccess),
          );
        }

        return newAcc;
      }
    }

    return acc;
  }, undefined);
}

export const Route = createRootRouteWithContext<{
  apiContext: ReturnType<typeof useActiveRealmAPIContext>;
  history: RouterHistory;
  isAuthenticated: boolean;
  isAuthenticating: boolean;
  queryClient: QueryClient;
  tabGroups: PortalTabbedNavigationGroups;
}>()({
  shouldReload: true,
  preload: false,
  beforeLoad: async function beforeLoad(opts) {
    const authRequirements = reduceMatchAuthRequirements(opts.matches);
    return { authRequirements } as const;
  },
  loader: async function loader({ context, location }) {
    if (context.authRequirements) {
      if (context.authRequirements.guard.auth) {
        const redirectToParam = getRedirectParameter(decodeURIComponent(location.href));

        await routeGuardRequireAuthentication(
          context.authRequirements.guard.auth,
          redirectToParam ? { redirectTo: redirectToParam, ...context.authRequirements.additionalRedirectContext } : undefined,
        );
      }

      if (context.authRequirements.guard.access) {
        await routeGuardValidRealmAccess(context.authRequirements.guard.access);
      }
    }
  },
  head: () => ({
    meta: [
      {
        title: 'Interchange',
      },
    ],
  }),
  component: Root,
});

export function Root() {
  const { startLoading, endLoading } = useFrameLoader();
  const { status } = Route.useMatch();

  useEffect(() => {
    if (status === 'pending') {
      startLoading();
    } else {
      endLoading();
    }
  }, [status, startLoading, endLoading]);

  return (
    <>
      <HeadContent />
      <Outlet />
    </>
  );
}
